const truncateFilename = (url: string, maxLength: number) => {
  const filename = url.split("/").pop();

  if (!filename) {
    return filename;
  }

  if (filename.length <= maxLength) return filename;

  return [
    filename.substr(0, maxLength / 2),
    filename.substr(filename.length - maxLength / 2),
  ].join("…");
};

const formatBytes = (bytes: number, decimals: number = 2) => {
  if (bytes === 0) return "0 Bytes";

  const k = 1024;
  const dm = decimals < 0 ? 0 : decimals;
  const sizes = ["Bytes", "KB", "MB", "GB", "TB", "PB", "EB", "ZB", "YB"];

  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + " " + sizes[i];
};

const getVideoDuration = (file: File): Promise<number> => {
  const video = document.createElement("video");

  const promise = new Promise<number>((resolve, reject) => {
    video.addEventListener("loadedmetadata", () => {
      // Chrome bug: https://bugs.chromium.org/p/chromium/issues/detail?id=642012
      if (video.duration === Infinity) {
        video.currentTime = Number.MAX_SAFE_INTEGER;
        video.ontimeupdate = () => {
          video.ontimeupdate = null;
          resolve(video.duration);
          video.currentTime = 0;
        };
      } else {
        resolve(video.duration);
      }
    });

    video.onerror = (event: any) => reject(event.target.error);
  });

  video.src = window.URL.createObjectURL(file);

  return promise;
};

const domParser = new DOMParser();

enum VideoProviders {
  RUMBLE = "rumble.com",
}

const getEmbedUrl = (html: string): string | undefined => {
  const document = domParser.parseFromString(html, "text/html").documentElement;
  const iframe = document.querySelector("iframe");

  if (iframe) {
    return iframe.src;
  }
};

/** Try adding autoplay to an iframe embed for platforms such as YouTube. */
const addAutoPlay = (html: string): string => {
  try {
    const document = domParser.parseFromString(
      html,
      "text/html",
    ).documentElement;
    const iframe = document.querySelector("iframe");

    if (iframe) {
      const url = new URL(iframe.src);
      const provider = new URL(iframe.src).host;

      if (provider === VideoProviders.RUMBLE) {
        url.searchParams.append("pub", "7a20");
        url.searchParams.append("autoplay", "2");
      } else {
        url.searchParams.append("autoplay", "1");
        url.searchParams.append("auto_play", "1");
        iframe.allow = "autoplay";
      }

      iframe.src = url.toString();

      // DOM parser creates html/body elements around original HTML fragment,
      // so we need to get innerHTML out of the body and not the entire document
      return (document.querySelector("body") as HTMLBodyElement).innerHTML;
    }
  } catch {
    return html;
  }

  return html;
};

const formatTime = (secondsNum: number): string => {
  let hours: number | string = Math.floor(secondsNum / 3600);
  let minutes: number | string = Math.floor((secondsNum - hours * 3600) / 60);
  let seconds: number | string = secondsNum - hours * 3600 - minutes * 60;

  if (hours < 10) hours = "0" + hours;
  if (minutes < 10) minutes = "0" + minutes;
  if (seconds < 10) seconds = "0" + seconds;

  return (hours === "00" ? "" : `${hours}:`) + `${minutes}:${seconds}`;
};

const findElementPosition = (el: HTMLElement) => {
  let box;

  if (el.getBoundingClientRect && el.parentNode) {
    box = el.getBoundingClientRect();
  }

  if (!box) {
    return {
      left: 0,
      top: 0,
    };
  }

  const docEl = document.documentElement;
  const body = document.body;

  const clientLeft = docEl.clientLeft || body.clientLeft || 0;
  const scrollLeft = window.pageXOffset || body.scrollLeft;
  const left = box.left + scrollLeft - clientLeft;

  const clientTop = docEl.clientTop || body.clientTop || 0;
  const scrollTop = window.pageYOffset || body.scrollTop;
  const top = box.top + scrollTop - clientTop;

  return {
    left: Math.round(left),
    top: Math.round(top),
  };
};

type Position = { x: number; y: number };

const getPointerPosition = (
  el: HTMLElement,
  event: MouseEvent & TouchEvent,
): Position => {
  const box = findElementPosition(el);
  const boxW = el.offsetWidth;
  const boxH = el.offsetHeight;
  const boxY = box.top;
  const boxX = box.left;

  let pageY = event.pageY;
  let pageX = event.pageX;

  if (event.changedTouches) {
    pageX = event.changedTouches[0].pageX;
    pageY = event.changedTouches[0].pageY;
  }

  return {
    y: Math.max(0, Math.min(1, (pageY - boxY) / boxH)),
    x: Math.max(0, Math.min(1, (pageX - boxX) / boxW)),
  };
};

const fileNameFromURL = (str: string) => {
  const url = new URL(str);
  const pathname = url.pathname;
  const index = pathname.lastIndexOf("/");

  return pathname.substring(index + 1);
};

export {
  getVideoDuration,
  formatBytes,
  formatTime,
  fileNameFromURL,
  getPointerPosition,
  truncateFilename,
  addAutoPlay,
  getEmbedUrl,
};
